/**
 * Environment validation.
 *
 * Configuration is validated once, at process start, and then handed around as
 * a frozen, fully-typed object. Nothing else in the codebase reads
 * `process.env` (an ESLint rule enforces this).
 *
 * Two properties matter more than convenience here:
 *
 *  - **Fail fast.** A missing `SUPABASE_SERVICE_ROLE_KEY` must crash the
 *    container on boot with a clear message, not surface as a confusing 500
 *    three days later when someone uploads their first bank statement.
 *  - **Never echo values.** The failure report names the offending variables
 *    and says what was expected. It never prints what was actually supplied,
 *    because that is precisely how secrets end up in a deploy log.
 */
export class EnvironmentError extends Error {
    issues;
    constructor(context, issues) {
        super([
            `Invalid ${context} environment configuration:`,
            ...issues.map((issue) => `  • ${issue}`),
            '',
            'Fix the variables above and restart. See .env.example for the full list.',
        ].join('\n'));
        this.name = 'EnvironmentError';
        this.issues = issues;
    }
}
/**
 * Parses and freezes configuration, or throws `EnvironmentError`.
 *
 * The result is deeply frozen so a stray assignment cannot mutate config at
 * runtime and produce behaviour that no test can reproduce.
 */
export function createEnv(options) {
    const result = options.schema.safeParse(options.source);
    if (!result.success) {
        const issues = result.error.issues.map((issue) => {
            const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
            return `${path}: ${issue.message}`;
        });
        throw new EnvironmentError(options.context, issues);
    }
    return Object.freeze(result.data);
}
//# sourceMappingURL=create-env.js.map